/**
 * The person page — Steps 53 and 130, built as ONE template.
 *
 * Kai: *"we also want the same for agents, it will be one template, just with
 * more agent info like what groups they're in."* So there is one route,
 * `/people/:id`, and the page adapts rather than forking:
 *
 *  - a requester has a **history** — what they have asked for — so Raised
 *    leads and the directory card sits beside it;
 *  - an agent has a **workload** — what they are holding — so Assigned leads,
 *    and they additionally get groups and roles, which is the "more agent
 *    info" half.
 *
 * Two pages would have been two shapes to keep in step for one job, and the
 * interesting cases are exactly the ones that cross: an agent raising their
 * own task tickets, or a requester who somehow has a ticket assigned to them
 * (which is a mis-assignment, and a page that hid it would be hiding a bug).
 *
 * **The ticket lists are the queue, filtered.** Same `/api/tickets`, same
 * shared condition grammar, so a count here cannot disagree with the list it
 * claims to summarise and "See all" is a real, editable queue filter rather
 * than a dead end. Exactly the reasoning Step 50 used for dashboard widgets.
 */
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, CircleUser, Mail, MapPin, Phone, ShieldCheck, Users } from 'lucide-react';
import type { Condition } from '@itsm/shared';
import { ticketRef } from '@itsm/shared';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { apiFetch } from '../lib/api';
import { qk } from '../lib/queryClient';
import { useUrlState } from '../lib/urlState';
import { fullTime, relativeTime } from '../lib/time';
import { cn } from '../lib/utils';
import { ticketPath, ticketsApi, type TicketList } from '../features/tickets/api';

interface PersonRecord {
  id: string;
  name: string | null;
  email: string;
  kind: string;
  isActive: number;
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  employeeId: string | null;
  employeeType: string | null;
  companyName: string | null;
  city: string | null;
  country: string | null;
  mobilePhone: string | null;
  businessPhone: string | null;
  managerName: string | null;
  userType: string | null;
  employeeHireDate: number | null;
  lastLoginAt: number | null;
  createdAt: number;
  entraSyncedAt: number | null;
  entraOid: string | null;
}

interface PersonPayload {
  person: PersonRecord;
  groups: { id: string; name: string }[];
  roles: { id: string; key: string; name: string }[];
  stats: { raisedOpen: number; raisedTotal: number; assignedOpen: number; assignedTotal: number };
}

const KIND_LABEL: Record<string, string> = {
  agent: 'Agent',
  platform: 'Platform staff',
  requester: 'End user',
};

/** Staff tiers. The same line every other surface draws — `/api/org/people`
 *  treats `platform` as an agent for assignment, and so does this page. */
const isStaff = (kind: string) => kind === 'agent' || kind === 'platform';

/** The filter behind each tab, and behind its "See all" link — one definition,
 *  so the embedded list and the queue cannot drift apart. */
function tabFilter(tab: 'raised' | 'assigned', personId: string): Condition {
  return { op: 'eq', fieldKey: tab === 'raised' ? 'requesterId' : 'assigneeId', value: personId };
}

export function PersonPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: qk.person(id),
    queryFn: () => apiFetch<PersonPayload>(`/api/org/people/${id}`),
    enabled: !!id,
  });

  const person = q.data?.person;
  const staff = !!person && isStaff(person.kind);
  // Which list leads is the ONLY thing the two variants disagree about, and
  // it is a default rather than a restriction — either tab is one click away
  // for either kind.
  const [tab, setTab] = useUrlState('list', '');
  const activeTab: 'raised' | 'assigned' =
    tab === 'assigned' || tab === 'raised' ? tab : staff ? 'assigned' : 'raised';

  const filter = useMemo(() => JSON.stringify(tabFilter(activeTab, id)), [activeTab, id]);
  const listParams = useMemo(() => {
    const p = new URLSearchParams({ filter, limit: '25', sort: 'updatedAt', dir: 'desc' });
    // Every type, problems and changes included. The default queue hides
    // those because they are not "the queue", but they ARE this person's
    // work, and a workload that silently omits their changes is wrong.
    p.set('type', 'incident,service_request,task,problem,change');
    return p;
  }, [filter]);

  const list = useQuery({
    queryKey: qk.tickets.list(listParams.toString()),
    queryFn: () => ticketsApi.list(listParams),
    enabled: !!id && !!person,
  });

  if (q.isPending) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (q.isError || !person) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-destructive">
              {/* A wrong-tenant id 404s exactly like a deleted one, on purpose. */}
              Could not load this person
            </p>
            <p className="text-sm text-muted-foreground">
              They may have been removed, or the link may be wrong.
            </p>
            <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = q.data!.stats;
  const groups = q.data!.groups;
  const roles = q.data!.roles;
  const display = person.name ?? person.email;
  const rows = list.data?.rows ?? [];
  const statusName = (key: string | null) =>
    (list.data as TicketList | undefined)?.statuses.find((s) => s.key === key)?.name ?? key ?? '—';

  const facts: { icon: typeof Mail; label: string; value: string; href?: string }[] = [
    { icon: Mail, label: 'Email', value: person.email, href: `mailto:${person.email}` },
    ...(person.businessPhone
      ? [
          {
            icon: Phone,
            label: 'Work phone',
            value: person.businessPhone,
            href: `tel:${person.businessPhone}`,
          },
        ]
      : []),
    ...(person.mobilePhone
      ? [{ icon: Phone, label: 'Mobile', value: person.mobilePhone, href: `tel:${person.mobilePhone}` }]
      : []),
    ...(person.officeLocation || person.city || person.country
      ? [
          {
            icon: MapPin,
            label: 'Location',
            value: [person.officeLocation, person.city, person.country].filter(Boolean).join(' · '),
          },
        ]
      : []),
    ...(person.companyName ? [{ icon: Building2, label: 'Company', value: person.companyName }] : []),
    ...(person.managerName ? [{ icon: CircleUser, label: 'Manager', value: person.managerName }] : []),
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      {/* Identity, then the two numbers that say what this person is to the
          service desk. Everything else is detail. */}
      <div className="flex flex-wrap items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand/15 text-lg font-medium text-brand">
          {display.slice(0, 1).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold">{display}</h1>
            <Badge variant="outline">{KIND_LABEL[person.kind] ?? person.kind}</Badge>
            {/* Deactivated is the single most load-bearing fact on this page:
                it explains an unanswered ticket and a failed assignment. */}
            {!person.isActive && (
              <Badge variant="outline" className="border-destructive/50 text-destructive">
                Deactivated
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {[person.jobTitle, person.department].filter(Boolean).join(' · ') || person.email}
          </p>
        </div>
        <div className="flex gap-2">
          <StatTile
            label="Raised"
            open={stats.raisedOpen}
            total={stats.raisedTotal}
            active={activeTab === 'raised'}
            onClick={() => setTab('raised')}
          />
          <StatTile
            label="Assigned"
            open={stats.assignedOpen}
            total={stats.assignedTotal}
            active={activeTab === 'assigned'}
            onClick={() => setTab('assigned')}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium">
              {activeTab === 'raised' ? 'Tickets they raised' : 'Tickets assigned to them'}
            </h2>
            <div className="flex-1" />
            {/* The same filter this list is built from, handed to the queue —
                so it opens editable rather than as a fixed report. */}
            <Link
              to={`/tickets?filter=${encodeURIComponent(filter)}`}
              className="rounded text-xs font-medium text-brand underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              See all in the queue
            </Link>
          </div>

          {list.isError && (
            <Card>
              <CardContent className="space-y-2 p-4">
                <p className="text-sm font-medium text-destructive">Could not load their tickets</p>
                <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          )}
          {list.isPending && <Skeleton className="h-40 w-full" />}
          {list.data && rows.length === 0 && (
            <Card>
              <CardContent className="p-10 text-center text-sm text-muted-foreground">
                {activeTab === 'raised' ? 'They have not raised anything.' : 'Nothing is assigned to them.'}
              </CardContent>
            </Card>
          )}
          {rows.length > 0 && (
            <Card>
              <CardContent className="p-0">
                {rows.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => navigate(ticketPath(t.type, t.id))}
                    className="flex w-full items-center gap-3 border-b px-3 py-2 text-left last:border-b-0 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  >
                    <span className="w-20 shrink-0 font-mono text-xs text-muted-foreground">
                      {ticketRef(t.type, t.number)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{t.subject}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {statusName(t.status)} · updated {relativeTime(t.updatedAt)}
                        {activeTab === 'raised' && t.assigneeName ? ` · ${t.assigneeName}` : ''}
                        {activeTab === 'assigned' ? ` · from ${t.requester.name ?? t.requester.email}` : ''}
                      </span>
                    </span>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
          {list.data && list.data.total > rows.length && (
            <p className="text-xs text-muted-foreground">
              Showing the {rows.length} most recently updated of {list.data.total}.
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Card>
            <CardContent className="space-y-2 p-3">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Details</p>
              {facts.map((f) => (
                <div key={f.label} className="flex items-start gap-2 text-sm">
                  <f.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{f.label}</p>
                    {f.href ? (
                      <a href={f.href} className="block break-words text-brand hover:underline">
                        {f.value}
                      </a>
                    ) : (
                      <p className="break-words">{f.value}</p>
                    )}
                  </div>
                </div>
              ))}
              {(person.employeeId || person.employeeType || person.userType) && (
                <p className="pt-1 text-xs text-muted-foreground">
                  {[person.employeeId && `ID ${person.employeeId}`, person.employeeType, person.userType]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* The "more agent info" half (Kai). Rendered only for staff: a
              requester is in no groups and holds no roles, and two empty
              cards would read as something being broken. */}
          {staff && (
            <Card>
              <CardContent className="space-y-3 p-3">
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    <Users className="h-3.5 w-3.5" /> Groups
                  </p>
                  {groups.length ? (
                    <div className="flex flex-wrap gap-1">
                      {groups.map((g) => (
                        <Badge key={g.id} variant="outline">
                          {g.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      In no groups — nothing routes to them automatically.
                    </p>
                  )}
                </div>
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    <ShieldCheck className="h-3.5 w-3.5" /> Roles
                  </p>
                  {roles.length ? (
                    <div className="flex flex-wrap gap-1">
                      {roles.map((r) => (
                        <Badge key={r.id} variant="outline">
                          {r.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No roles — they can see nothing.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="space-y-1.5 p-3 text-xs">
              <p className="font-medium uppercase tracking-wider text-muted-foreground">Access</p>
              <p className="text-muted-foreground">
                {person.lastLoginAt ? (
                  <>
                    Last signed in <span className="text-foreground">{relativeTime(person.lastLoginAt)}</span>
                  </>
                ) : (
                  // For a requester this is the normal state, not a fault:
                  // they raise tickets by email and never sign in at all.
                  'Never signed in'
                )}
              </p>
              <p className="text-muted-foreground">Known since {fullTime(person.createdAt)}</p>
              {/* Never-synced and stale-synced are different problems, so they
                  read differently — the schema comment insists on it. */}
              <p className="text-muted-foreground">
                {person.entraSyncedAt
                  ? `Directory synced ${relativeTime(person.entraSyncedAt)}`
                  : person.entraOid
                    ? 'Signed in with Microsoft; profile not synced yet'
                    : 'Not linked to the directory'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Open over all-time. The open number is what someone came here to find; the
 *  total is context, so it is smaller and quieter rather than a second tile. */
function StatTile({
  label,
  open,
  total,
  active,
  onClick,
}: {
  label: string;
  open: number;
  total: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'min-w-24 rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-brand/50 bg-brand/5' : 'bg-card hover:bg-accent/50',
      )}
    >
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span className="block text-xl font-semibold leading-tight">{open}</span>
      {/* The big number is the OPEN one, and it has to SAY so — read alone it
          is indistinguishable from a total, which is the other number on the
          same tile. */}
      <span className="block text-xs text-muted-foreground">open · {total} all time</span>
    </button>
  );
}
